import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiDialogComponent,
  OuiDialogHeaderComponent,
  OuiGridComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiSymbolComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { css } from '@emotion/css';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CheckboxComponent } from '../../components/checkbox/checkbox.component';
import { InputComponent } from '../../components/input/input.component';
import { SelectComponent } from '../../components/select/select.component';
import { SalesChannel } from '../../models/sales-channel.model';
import { Warehouse, WarehouseChannelAllocation, CreateChildWarehouseRequest } from '../../models/warehouse.models';
import { RegionService } from '../../services/region/region.service';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { BasePageComponent } from '../base/base.component';

@Component({
  selector: 'app-warehouse-details',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiButtonComponent,
    OuiCenterComponent,
    OuiDialogComponent,
    OuiDialogHeaderComponent,
    OuiGridComponent,
    OuiRowComponent,
    OuiStackComponent,
    OuiSymbolComponent,
    OuiTextComponent,
    InputComponent,
    SelectComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './warehouse-details.component.html',
})
export class WarehouseDetailsPage extends BasePageComponent {
  private readonly warehouseService = inject(WarehouseService);
  private readonly salesChannelService = inject(SalesChannelService);
  private readonly regionService = inject(RegionService);
  private readonly notification = inject(NotificationService);
  private readonly currentWarehouseCode = signal('');

  readonlyForm = new FormGroup({
    code: new FormControl({ value: '', disabled: true }),
    name: new FormControl({ value: '', disabled: true }),
    active: new FormControl({ value: '', disabled: true }),
    entity: new FormControl({ value: '', disabled: true }),
    dcc: new FormControl({ value: '', disabled: true }),
    currency: new FormControl({ value: '', disabled: true }),
    nonAuctionSubInvCode: new FormControl({ value: '', disabled: true }),
    wms: new FormControl({ value: '', disabled: true }),
    shippingFeeSku: new FormControl({ value: '', disabled: true }),
    auctionSubInvCode: new FormControl({ value: '', disabled: true }),
    gradingCode: new FormControl({ value: '', disabled: true }),
    warehouseInventoryCode: new FormControl({ value: '', disabled: true }),
    comments: new FormControl({ value: '', disabled: true }),
  });

  warehouse = signal<Warehouse | undefined>(undefined);

  showCreateChildDialog = signal<boolean>(false);

  currencyOptions = [
    { label: 'USD', value: 'USD' },
    { label: 'CAD', value: 'CAD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'GBP', value: 'GBP' },
  ];

  defaultShippingOptions = [
    { label: 'Assurant Shipping', value: 'AssurantShipping' },
    { label: 'Schedule Pickup', value: 'SchedulePickup' },
    { label: 'Ship From My Account', value: 'ShipFromMyAccount' },
  ];

  createChildForm = new FormGroup({
    code: new FormControl('', Validators.required),
    childWarehouseName: new FormControl('', Validators.required),
    currency: new FormControl('', Validators.required),
    orderConsolidationDays: new FormControl('', [Validators.required, Validators.pattern('^[0-9]+$')]),
    defaultShippingOption: new FormControl(''),
    assurantShipping: new FormControl(false),
    schedulePickup: new FormControl(false),
    shipFromMyAccount: new FormControl(false),
    auctionOrderType: new FormControl('', Validators.required),
    nonAuctionOrderTypes: new FormControl('', Validators.required),
  });

  isCreateChildSaving = false;

  openCreateChildDialog() {
    this.createChildForm.reset();
    this.showCreateChildDialog.set(true);
  }

  closeCreateChildDialog() {
    this.showCreateChildDialog.set(false);
  }

  handleCreateChildDialogShow(showing: boolean) {
    if (!showing) {
      this.showCreateChildDialog.set(false);
    }
  }

  saveCreateChild() {
    this.createChildForm.markAllAsTouched();
    if (this.createChildForm.invalid || this.isCreateChildSaving) {
      return;
    }

    const parentCode = this.warehouse()?.code;
    if (!parentCode) {
      return;
    }

    const v = this.createChildForm.getRawValue();

    const selectedMethods: string[] = [];
    if (v.assurantShipping) selectedMethods.push('AssurantShipping');
    if (v.schedulePickup) selectedMethods.push('SchedulePickup');
    if (v.shipFromMyAccount) selectedMethods.push('ShipFromMyAccount');

    const payload: CreateChildWarehouseRequest = {
      code: v.childWarehouseName ?? '',
      name: v.childWarehouseName ?? '',
      currency_code: v.currency ?? '',
      order_consolidation_days: Number(v.orderConsolidationDays),
      default_shipping_method: v.defaultShippingOption || null,
      allowed_shipping_methods: selectedMethods.length ? selectedMethods.join('|') : null,
      warehouse_sub_inventory_codes: v.code ? [v.code] : [],
      order_type_code_auction: v.auctionOrderType?.trim() || null,
      order_type_code_others: v.nonAuctionOrderTypes?.trim() || null,
    };

    this.isCreateChildSaving = true;
    this.warehouseService
      .createChildWarehouse(parentCode, payload)
      .pipe(finalize(() => { this.isCreateChildSaving = false; }))
      .subscribe({
        next: () => {
          this.notification.showSnackbar({
            message: 'Child warehouse created successfully',
            variant: 'success',
            symbolName: 'check_circle',
          });
          this.closeCreateChildDialog();
          this.warehouseService.warehouses.reload();
        },
        error: (err) => {
          const errorMessage =
            err?.error?.message || err?.message || 'Failed to create child warehouse';
          this.notification.showSnackbar({
            message: errorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
        },
      });
  }

  inventoryAllocationChannels: SalesChannel[] = [];
  inventoryAllocationForm = new FormGroup({});

  isInventoryAllocationLoading = false;
  isInventoryAllocationSaving = false;

  private allocationControlNames: Record<string, string> = {};

  shippingOptionLabelClassName = css({
    fontFamily: "'Inter', sans-serif",
    fontWeight: 400,
    fontSize: '0.875rem',
  });

  commentsTextareaClassName = css({
    width: '100%',
    fontSize: '1rem',
    borderRadius: '4px',
    padding: '8px',
    border: 'solid 1px #b3b3b3',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 400,
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: '#f5f5f5',
    },
  });

  inventoryAllocationTableWrapperClassName = css({
    width: '100%',
    overflowX: 'auto',
    border: '1px solid #d9dde3',
    borderRadius: '0.5rem',
    backgroundColor: '#ffffff',
  });

  inventoryAllocationTableClassName = css({
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    '& th, & td': {
      borderBottom: '1px solid #e6e9ee',
      borderRight: '1px solid #e6e9ee',
      padding: '0.625rem 0.75rem',
    },
    '& th:last-child, & td:last-child': {
      borderRight: 'none',
    },
    '& tbody tr:last-of-type td': {
      borderBottom: 'none',
    },
    '& th': {
      backgroundColor: '#f4f6f8',
      textAlign: 'center',
    },
    '& td': {
      backgroundColor: '#ffffff',
    },
    '& td form': {
      margin: 0,
    },
    '& td input': {
      width: '100%',
      maxWidth: '5rem',
      textAlign: 'center',
      margin: '0 auto',
      display: 'block',
    },
  });

  relationshipLinkClassName = css({
    color: '#3e8aee',
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'inline-block',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: '1.5rem',
  });

  relationshipEmptyTextClassName = css({
    color: '#6b7280',
  });

  relationshipListClassName = css({
    maxHeight: '7.5rem',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingRight: '0.25rem',
  });

  headerActionButtonClassName = css({
    backgroundColor: '#17223A !important',
    color: '#ffffff !important',
    borderColor: '#17223A !important',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&&:hover, &&:focus, &&:focus-visible': {
      backgroundColor: '#3b5691 !important',
      borderColor: '#3b5691 !important',
    },
  });

  detailsGridTemplateColumns = computed(() => '3fr 1fr');

  gridLayout = computed(
    () => "'heading heading' 'details details' 'inventory relationship'",
  );

  childWarehouses = computed<Warehouse[]>(() => {
    const currentWarehouse = this.warehouse();

    if (!currentWarehouse) {
      return [];
    }

    return this.warehouseService
      .warehouses
      .value()
      .filter((warehouse) => warehouse.parentCode === currentWarehouse.code)
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  showRelationshipPanel = computed(() => {
    return !!this.warehouse();
  });

  isChildWarehouse = computed(() => !!this.warehouse()?.parentCode);

  parentWarehouse = computed<Warehouse | undefined>(() => {
    const parentCode = this.warehouse()?.parentCode;

    if (!parentCode) {
      return undefined;
    }

    return this.warehouseService
      .warehouses
      .value()
      .find((warehouse) => warehouse.code === parentCode);
  });

  shippingOptionsLabels = {
    AssurantShipping: 'Assurant Shipping',
    SchedulePickup: 'Schedule Pickup',
    ShipFromMyAccount: 'Ship From My Account',
  };

  shippingOptions = computed(() =>
    Object.entries(this.shippingOptionsLabels).map(([value, label]) => ({
      value,
      label,
    })),
  );

  shippingOptionsSelections = computed<string[]>(() => {
    const methods = (this.warehouse()?.allowedShippingMethods || '').trim();
    if (!methods) return [];

    return methods
      .split('|')
      .map((method) => method.trim())
      .filter(Boolean);
  });

  get warehouseCode(): string {
    return this.currentWarehouseCode();
  }

  @Input()
  set warehouseCode(warehouseCode: string) {
    if (!warehouseCode) {
      this.warehouse.set(undefined);
      this.currentWarehouseCode.set('');
      this.inventoryAllocationChannels = [];
      this.inventoryAllocationForm = new FormGroup({});
      this.allocationControlNames = {};
      return;
    }

    this.currentWarehouseCode.set(warehouseCode);

    this.warehouseService.getWarehouseById(warehouseCode).subscribe((warehouse) => {
      this.warehouse.set(warehouse);

      this.readonlyForm.patchValue({
        code: warehouse.code || '',
        name: warehouse.name || '',
        active: warehouse.activeFlag ? 'Yes' : 'No',
        entity: warehouse.entityCode || '',
        dcc: warehouse.dccCode || '',
        currency: warehouse.currencyCode || '',
        nonAuctionSubInvCode: warehouse.subInvCodeOthers || '',
        wms: warehouse.wmsCode || '',
        shippingFeeSku: warehouse.shippingFeeSku || '',
        auctionSubInvCode: warehouse.subInvCodeAuction || '',
        gradingCode: warehouse.gradingCode || '',
        warehouseInventoryCode: '',
        comments: warehouse.comments || '',
      });

      if (warehouse.parentCode) {
        this.warehouseService
          .getChildSubInventory(warehouse.code)
          .subscribe((codes) => {
            const firstCode = codes.filter(Boolean)[0] || '';

            this.readonlyForm.patchValue({
              warehouseInventoryCode: firstCode,
            });
          });
      }
    });
  }

  constructor() {
    super();

    effect(() => {
      const warehouseCode = this.currentWarehouseCode();
      const regionId = this.regionService.selectedRegionId();

      if (!warehouseCode || !regionId) {
        return;
      }

      this.loadInventoryAllocationConfiguration(warehouseCode, regionId);
    });
  }

  inventoryAllocationControl(channelCode: string) {
    return this.allocationControlNames[channelCode] || '';
  }

  allocationSumValidationMessage() {
    const sumExceeded = this.inventoryAllocationForm.errors?.[
      'allocationSumExceeded'
    ] as { total: number; max: number } | undefined;

    if (!sumExceeded) {
      return '';
    }

    return `Total inventory allocation cannot exceed ${sumExceeded.max}%. Current total is ${sumExceeded.total}%.`;
  }

  saveInventoryAllocationConfiguration() {
    const warehouseCode = this.currentWarehouseCode();
    this.inventoryAllocationForm.updateValueAndValidity();
    const totalAllocation = this.currentAllocationTotal(this.inventoryAllocationForm);

    if (totalAllocation > 100) {
      this.notification.showSnackbar({
        message: 'Total allocation cannot be greater than 100%',
        variant: 'warn',
        symbolName: 'error',
      });
      return;
    }

    if (
      !warehouseCode ||
      this.isInventoryAllocationSaving ||
      this.inventoryAllocationForm.invalid
    ) {
      return;
    }

    const allocations: WarehouseChannelAllocation[] = this.inventoryAllocationChannels.map(
      (channel) => ({
        channelCode: channel.code,
        inventoryAllocation: this.toAllocationValue(
          this.inventoryAllocationForm
            .get(this.inventoryAllocationControl(channel.code))
            ?.value,
        ),
      }),
    );

    this.isInventoryAllocationSaving = true;
    this.warehouseService
      .updateChannelAllocations(warehouseCode, allocations)
      .pipe(
        finalize(() => {
          this.isInventoryAllocationSaving = false;
        }),
      )
      .subscribe({
        next: (updatedAllocations) => {
          this.initializeInventoryAllocationForm(
            this.inventoryAllocationChannels,
            updatedAllocations,
          );
          this.notification.showSnackbar({
            message: 'Inventory allocation config is saved successfully',
            variant: 'success',
            symbolName: 'check_circle',
          });
        },
        error: (err) => {
          const errorMessage =
            err?.error?.message ||
            err?.message ||
            'Failed to save inventory allocation config';
          this.notification.showSnackbar({
            message: errorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
        },
      });
  }

  showCreateChildButton() {
    const warehouse = this.warehouse();
    return !!warehouse && !warehouse.parentCode;
  }

  private loadInventoryAllocationConfiguration(
    warehouseCode: string,
    regionId: number,
  ) {
    this.isInventoryAllocationLoading = true;

    forkJoin({
      channels: this.salesChannelService.getSalesChannelsByRegionId(regionId),
      allocations: this.warehouseService.getChannelAllocations(warehouseCode),
    })
      .pipe(
        finalize(() => {
          this.isInventoryAllocationLoading = false;
        }),
      )
      .subscribe(({ channels, allocations }) => {
        this.inventoryAllocationChannels = channels;
        this.initializeInventoryAllocationForm(channels, allocations);
      });
  }

  private initializeInventoryAllocationForm(
    channels: SalesChannel[],
    allocations: WarehouseChannelAllocation[],
  ) {
    const allocationByChannel = new Map(
      allocations.map((allocation) => [
        allocation.channelCode,
        allocation.inventoryAllocation,
      ]),
    );

    const controls: Record<string, FormControl<string>> = {};
    const controlNames: Record<string, string> = {};

    channels.forEach((channel, index) => {
      const controlName = this.getAllocationControlName(channel.code, index);
      controlNames[channel.code] = controlName;

      const value = allocationByChannel.get(channel.code) ?? 0;
      controls[controlName] = new FormControl(`${value}%`, {
        nonNullable: true,
      });
    });

    this.allocationControlNames = controlNames;
    this.inventoryAllocationForm = new FormGroup(controls, {
      validators: [this.maxAllocationSumValidator()],
    });
  }

  private maxAllocationSumValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }

      const totalAllocation = this.currentAllocationTotal(control);

      if (totalAllocation > 100) {
        return {
          allocationSumExceeded: {
            total: totalAllocation,
            max: 100,
          },
        };
      }

      return null;
    };
  }

  private currentAllocationTotal(control: FormGroup) {
    return Object.values(control.controls).reduce(
      (sum, allocationControl) =>
        sum + this.parseAllocationValue(allocationControl.value),
      0,
    );
  }

  private getAllocationControlName(channelCode: string, index: number) {
    return `allocation_${channelCode.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${index}`;
  }

  private parseAllocationValue(value: unknown) {
    const parsed = Number(String(value ?? '').replace('%', '').trim());

    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }

    return parsed;
  }

  private toAllocationValue(value: unknown) {
    const parsed = this.parseAllocationValue(value);

    if (parsed > 100) {
      return 100;
    }

    return parsed;
  }
}